import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getMyRole } from "@/lib/reservations.functions";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { AdminBottomNav } from "@/components/admin/AdminBottomNav";
import { OfflineBanner } from "@/components/admin/OfflineBanner";
import { registerAdminServiceWorker } from "@/lib/register-admin-sw";
import { ADMIN_APP_LINKS, ADMIN_APP_META } from "@/lib/admin-app-head";
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  ClipboardList,
  Shield,
  CreditCard,
  Map,
  Settings,
  FileText,
  UserCog,
  LogOut,
  ListChecks,
  Menu,
  X,
  UtensilsCrossed,
  Sparkles,
  Mic,
  Instagram,

} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Plunkets Admin" },
      { name: "robots", content: "noindex" },
      ...ADMIN_APP_META,
    ],
    links: ADMIN_APP_LINKS,
  }),
  component: AdminLayout,
});

const NAV: { to: string; label: string; icon: typeof LayoutDashboard; exact?: boolean; roles?: string[] }[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/ai", label: "AI Manager", icon: Sparkles, roles: ["owner", "manager"] },
  { to: "/admin/reservations", label: "Reservations", icon: ClipboardList },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },


  { to: "/admin/floor-plan", label: "Floor plan", icon: Map, roles: ["owner", "manager", "host", "door"] },
  { to: "/admin/waitlist", label: "Waitlist", icon: ListChecks, roles: ["owner", "manager", "host", "door"] },
  { to: "/admin/guests", label: "Guests", icon: Users, roles: ["owner", "manager", "host"] },
  { to: "/admin/booking-control", label: "Booking control", icon: Shield, roles: ["owner", "manager"] },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, roles: ["owner", "manager"] },
  { to: "/admin/events", label: "Events", icon: CalendarDays, roles: ["owner", "manager"] },
  { to: "/admin/karaoke", label: "Karaoke", icon: Mic, roles: ["owner", "manager", "host"] },
  { to: "/admin/instagram", label: "Instagram", icon: Instagram, roles: ["owner", "manager", "host"] },
  { to: "/admin/menu", label: "Menu & Cocktails", icon: UtensilsCrossed, roles: ["owner", "manager"] },
  { to: "/admin/reports", label: "Reports", icon: FileText, roles: ["owner", "manager"] },
  { to: "/admin/settings", label: "Settings", icon: Settings, roles: ["owner", "manager"] },
  { to: "/admin/team", label: "Team", icon: UserCog, roles: ["owner"] },
  { to: "/admin/audit-log", label: "Audit log", icon: FileText, roles: ["owner", "manager"] },
];


const BOTTOM = ["/admin", "/admin/reservations", "/admin/calendar", "/admin/floor-plan"];

function AdminLayout() {
  const roleFn = useServerFn(getMyRole);
  const { data: roleData } = useQuery({
    queryKey: ["my-role"],
    queryFn: () => roleFn(),
  });
  const nav = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    registerAdminServiceWorker();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [path]);

  // Lock scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  async function signOut() {
    await supabase.auth.signOut();
    nav({ to: "/auth" });
  }

  const noAccess =
    roleData &&
    !roleData.roles.some((r) => ["owner", "manager", "host", "door", "floor"].includes(r));

  const userRoles = roleData?.roles ?? [];
  const visibleNav = NAV.filter(
    (n) => !n.roles || n.roles.some((r) => userRoles.includes(r)),
  );

  const activeLabel = NAV.find((n) =>
    n.exact ? path === n.to : path.startsWith(n.to)
  )?.label ?? "Admin";

  const SidebarContent = (
    <>
      <Link to="/admin" className="block">
        <img
          src="/__l5e/assets-v1/8e4e1149-6c7d-4be6-94ba-348c7e90e75c/plunkets-horizontal.png"
          alt="Plunkets"
          className="h-10 w-auto"
        />
        <span className="mt-1 block text-[10px] uppercase tracking-[0.3em] text-primary">Admin Console</span>
      </Link>
      <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {roleData?.roles.join(" · ") ?? "…"}
      </p>
      <nav className="mt-6 space-y-1">
        {visibleNav.map((n) => {
          const Icon = n.icon;
          const active = n.exact ? path === n.to : path.startsWith(n.to);
          return (
            <Link
              key={n.to}
              to={n.to as "/admin"}
              className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition ${
                active
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
              }`}
            >
              <Icon size={15} /> {n.label}
            </Link>
          );
        })}
      </nav>
      <button
        onClick={signOut}
        className="mt-6 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <LogOut size={15} /> Sign out
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0a1518] text-foreground">
      <OfflineBanner />
      {/* Mobile top bar */}
      <header style={{ paddingTop: "env(safe-area-inset-top)", boxSizing: "content-box" }} className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-[#0F1E22]/95 px-4 backdrop-blur md:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="rounded-md p-2 text-foreground hover:bg-muted/30"
        >
          <Menu size={20} />
        </button>
        <span className="truncate text-sm font-semibold uppercase tracking-wider text-primary">
          {activeLabel}
        </span>
        <button
          onClick={signOut}
          aria-label="Sign out"
          className="rounded-md p-2 text-muted-foreground hover:text-foreground"
        >
          <LogOut size={18} />
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside style={{ paddingTop: "max(1rem, env(safe-area-inset-top))", paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }} className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col overflow-y-auto border-r border-border bg-[#0F1E22] p-4">
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              className="mb-2 ml-auto rounded-md p-2 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
            {SidebarContent}
          </aside>
        </div>
      )}

      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 overflow-y-auto border-r border-border bg-[#0F1E22]/80 p-4 md:block">
          {SidebarContent}
        </aside>
        <main className="min-h-screen w-full min-w-0 flex-1 p-4 pb-24 sm:p-6 sm:pb-24 md:p-10">
          {noAccess ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Your account has no staff role assigned. Contact an Owner.
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <AdminBottomNav
        items={visibleNav.filter((n) => BOTTOM.includes(n.to))}
        path={path}
        onMore={() => setMobileOpen(true)}
      />
    </div>
  );
}
